/**
 * SaGó · qué se enseña y qué se añade de «Se suele pedir con esto».
 *
 * Publica dos eventos propios con Shopify.analytics.publish, la vía oficial
 * para que los recoja un píxel personalizado (Configuración → Eventos de
 * clientes) y los mande a GA4 y, más adelante, al ERP. Este script no pinta
 * nada ni pide nada a la tienda: sólo avisa. Si Shopify no ha cargado su
 * gestor de píxeles (el editor del tema, un bloqueador), no hace nada.
 *
 *   recs_impresion  — el bloque se ha visto (la mitad, con el cajón abierto)
 *                     o, si es más alto que la zona que se ve, ocupa al
 *                     menos el 40 % de la pantalla
 *   recs_click      — se ha añadido uno de sus productos (Shopify dijo que sí)
 *
 * Los datos salen del JSON que pinta Liquid dentro del bloque
 * (.sago-recs-datos, en snippets/sago-cart-upsell.liquid): el script no
 * decide nada, cuenta lo que Liquid ya decidió. Importes en euros.
 *
 * UNA impresión por combinación de productos y reglas en cada apertura del
 * cajón. Dawn vuelve a pintar el cajón en cada cambio (al añadir, al cambiar
 * cantidades, el calendario tras sus propios POST): contar cada repintado
 * inflaría las impresiones. Al cerrar el cajón se empieza de cero.
 *
 * Los MutationObserver de aquí sólo LEEN (buscan bloques nuevos, miran si el
 * cajón se cierra): nunca tocan el DOM, así que no pueden entrar en bucle con
 * lo que observan, que es lo que le pasaba al calendar-handler viejo.
 */
(() => {
  if (window.sagoRecsEventos) return;
  window.sagoRecsEventos = true;

  const cajon = document.querySelector('cart-drawer');
  if (!cajon || !('IntersectionObserver' in window)) return;

  const euros = (centimos) => Math.round(Number(centimos) || 0) / 100;

  const publicar = (nombre, datos) => {
    if (window.Shopify && window.Shopify.designMode) return false;
    const analytics = window.Shopify && window.Shopify.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return false;
    try {
      analytics.publish(nombre, datos);
      return true;
    } catch (e) {
      return false;
    }
  };

  const leer = (bloque) => {
    try {
      return JSON.parse(bloque.querySelector('.sago-recs-datos').textContent);
    } catch (e) {
      return null;
    }
  };

  const producto = (p) => ({
    product_id: p.product_id,
    variant_id: p.variant_id,
    regla: p.regla,
    posicion: p.posicion,
    precio: euros(p.precio),
  });

  const sobre = (datos) => ({
    origen: datos.origen,
    listas: datos.listas,
    subtotal: euros(datos.subtotal),
    moneda: datos.moneda,
    lineas: datos.lineas,
  });

  /* ── recs_impresion ─────────────────────────────────────────────────────── */
  // «Visto» es la mitad del bloque a la vista, o el 40 % de la pantalla si el
  // bloque no cabe (móvil en horizontal). NO basta `isIntersecting`: la
  // primera notificación de observe() llega aunque se vea un píxel, y un
  // bloque nuevo que aparece a medias tras un repintado se contaba al 10 %.
  // Por eso varios umbrales: para enterarse al cruzar la mitad.
  const seVe = (e) =>
    e.isIntersecting &&
    (e.intersectionRatio >= 0.5 ||
      (e.rootBounds && e.intersectionRect.height >= e.rootBounds.height * 0.4));

  let vistas = new Set();
  const observados = new WeakSet();

  const io = new IntersectionObserver(
    (entradas) => {
      for (const entrada of entradas) {
        if (!seVe(entrada) || !cajon.classList.contains('active')) continue;
        const datos = leer(entrada.target);
        if (!datos || !Array.isArray(datos.productos) || datos.productos.length === 0) continue;
        const clave = datos.productos.map((p) => `${p.product_id}:${p.regla}`).join(',');
        if (vistas.has(clave)) continue;
        if (publicar('recs_impresion', { ...sobre(datos), productos: datos.productos.map(producto) })) {
          vistas.add(clave);
        }
      }
    },
    { threshold: [0, 0.25, 0.5, 0.75, 1] }
  );

  // Sólo hay un bloque vivo a la vez: al llegar el nuevo se deja de observar el
  // viejo, que si no se queda retenido fuera del documento hasta recargar.
  let bloqueActual = null;
  const vigilarBloques = () => {
    const bloque = cajon.querySelector('[data-sago-recs]');
    if (!bloque || observados.has(bloque)) return;
    if (bloqueActual) io.unobserve(bloqueActual);
    observados.add(bloque);
    bloqueActual = bloque;
    io.observe(bloque);
  };

  new MutationObserver(vigilarBloques).observe(cajon, { childList: true, subtree: true });
  new MutationObserver(() => {
    if (!cajon.classList.contains('active')) vistas = new Set();
  }).observe(cajon, { attributes: true, attributeFilter: ['class'] });
  vigilarBloques();

  /* ── recs_click ─────────────────────────────────────────────────────────── */
  // Al enviar se apunta qué tarjeta fue; se publica cuando product-form.js
  // confirma el alta (cart-update) y se olvida si Shopify la rechaza
  // (cart-error). Así cuenta lo añadido, no lo pulsado.
  //
  // Es una COLA, no un solo apunte: pulsar las dos tarjetas seguidas (en unos
  // cientos de milisegundos, normal en el móvil) dejaba dos envíos en vuelo, el
  // segundo pisaba al primero y el primer «Añadir» no se contaba nunca.
  let pendientes = [];

  document.addEventListener(
    'submit',
    (ev) => {
      const form = ev.target;
      const bloque = form && form.closest && form.closest('[data-sago-recs]');
      if (!bloque) return;
      // Un segundo toque mientras va el primero: product-form.js lo descarta
      // (botón con aria-disabled) y aquí no debe quedar un apunte fantasma que
      // luego casaría con otra alta de la misma variante.
      if (form.querySelector('[type="submit"][aria-disabled="true"]')) return;
      const datos = leer(bloque);
      const variante = Number(new FormData(form).get('id'));
      const elegido = datos && datos.productos.find((p) => p.variant_id === variante);
      if (elegido) pendientes.push({ datos, elegido, hora: Date.now() });
    },
    true
  );

  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  const sacarPendiente = (ev) => {
    const ahora = Date.now();
    pendientes = pendientes.filter((p) => ahora - p.hora < 15000);
    if (!ev || ev.source !== 'product-form') return null;
    const i = pendientes.findIndex((p) => Number(ev.productVariantId) === p.elegido.variant_id);
    return i === -1 ? null : pendientes.splice(i, 1)[0];
  };

  // pubsub.js no aísla a quien se suscribe: si esto lanzara, product-form.js
  // saltaría a su catch sin repintar el cajón. Nada de aquí debería lanzar,
  // pero un fallo de medición no puede costar una venta.
  subscribe(PUB_SUB_EVENTS.cartUpdate, (ev) => {
    try {
      const p = sacarPendiente(ev);
      if (p) publicar('recs_click', { ...sobre(p.datos), producto: producto(p.elegido) });
    } catch (e) {}
  });

  subscribe(PUB_SUB_EVENTS.cartError, (ev) => {
    try {
      sacarPendiente(ev);
    } catch (e) {}
  });
})();
